import React, { useState } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS } from '../utils/permissions';
import { createNotification } from '../utils/notifications';
import Toast from '../components/common/Toast';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

const NotificationSettingsPage = () => {
  const { currentUser } = useAuth();
  const { can } = usePermissions(currentUser);
  
  // UI state
  const [toast, setToast] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  // Deadline check state
  const [showDeadlineCheckModal, setShowDeadlineCheckModal] = useState(false);
  const [deadlineCheckRunning, setDeadlineCheckRunning] = useState(false);
  const [deadlineCheckResult, setDeadlineCheckResult] = useState(null);
  const [deadlineCheckMode, setDeadlineCheckMode] = useState('dryRun'); // 'dryRun' | 'override' | 'live'
  const [deadlineCheckOverrideEmail, setDeadlineCheckOverrideEmail] = useState('');





  const testNotification = async (level) => {
    if (testing || !can(FEATURE_PERMISSIONS.NOTIFICATIONS_MANAGE)) return;
    setTesting(true);
    
    try {
      const levelLabels = {
        info: 'Info',
        success: 'Success',
        warning: 'Warning',
        urgent: 'Urgent'
      };
      
      await createNotification({
        title: `Test ${levelLabels[level]} Notification`,
        body: `This is a test ${level} notification created at ${new Date().toLocaleTimeString()}. It should appear in real-time in your notification dropdown!`,
        level,
        recipients: [currentUser.uid],
        related: { type: 'Test', id: 'test-123' }
      });
      
      setToast({ 
        message: `${levelLabels[level]} notification created! Check the bell icon.`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Failed to create test notification:', error);
      setToast({ 
        message: `Failed to create notification: ${error.message}`, 
        type: 'error' 
      });
    } finally {
      setTesting(false);
    }
  };

  const runDeadlineCheck = async () => {
    if (deadlineCheckMode === 'override' && !deadlineCheckOverrideEmail.trim()) {
      setToast({ message: 'Enter a recipient override email first.', type: 'warning' });
      return;
    }
    if (deadlineCheckMode === 'live') {
      const ok = window.confirm(
        'LIVE RUN: This will send aggregated digest emails to ALL managers with overdue/due-soon items right now. Continue?'
      );
      if (!ok) return;
    }
    setDeadlineCheckRunning(true);
    setDeadlineCheckResult(null);
    try {
      const fn = httpsCallable(functions, 'triggerDeadlineCheck');
      const params = {};
      if (deadlineCheckMode === 'dryRun') params.dryRun = true;
      if (deadlineCheckMode === 'override') params.recipientOverride = deadlineCheckOverrideEmail.trim();
      const res = await fn(params);
      setDeadlineCheckResult(res.data || null);
      const summary = res.data || {};
      const mgr = deadlineCheckMode === 'dryRun' ? (summary.managersQueued || 0) : (summary.managerEmailsSent || 0);
      const reps = deadlineCheckMode === 'dryRun' ? (summary.repsQueued || 0) : (summary.repEmailsSent || 0);
      const verb = deadlineCheckMode === 'dryRun' ? 'would be sent' : 'sent';
      setToast({ message: `✅ Deadline check complete — ${mgr} manager digest(s) + ${reps} rep digest(s) ${verb}.`, type: 'success' });
    } catch (err) {
      console.error('triggerDeadlineCheck failed:', err);
      setToast({ message: 'Failed: ' + (err.message || 'Unknown error'), type: 'error' });
    } finally {
      setDeadlineCheckRunning(false);
    }
  };

  const testEmailSubscription = async () => {
    if (testingEmail || !can(FEATURE_PERMISSIONS.NOTIFICATIONS_MANAGE)) return;
    
    if (!currentUser?.email) {
      setToast({ message: 'No email address found', type: 'error' });
      return;
    }
    
    setTestingEmail(true);
    try {
      const testFunction = httpsCallable(functions, 'testReportSubscriptions');
      await testFunction({ email: currentUser.email });
      setToast({ message: '✅ Test email sent! Check your inbox.', type: 'success' });
    } catch (err) {
      console.error('Test failed:', err);
      setToast({ message: 'Test failed: ' + err.message, type: 'error' });
    } finally {
      setTestingEmail(false);
    }
  };

  // Check permissions
  const isReadOnly = !can(FEATURE_PERMISSIONS.NOTIFICATIONS_MANAGE);
  
  if (!can(FEATURE_PERMISSIONS.NOTIFICATIONS_VIEW) && !can(FEATURE_PERMISSIONS.NOTIFICATIONS_MANAGE)) {
    return (
      <main className="flex-grow p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to view notification settings.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow p-6 overflow-y-auto">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* Admin: Trigger Deadline Check Modal */}
      {showDeadlineCheckModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold">Run Deadline Check (Admin)</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Manually invokes the daily manager-digest job. Use Dry Run or Recipient Override
                    to safely test without spamming managers.
                  </p>
                </div>
                <button
                  onClick={() => setShowDeadlineCheckModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  aria-label="Close"
                >&times;</button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="deadlineCheckMode"
                    value="dryRun"
                    checked={deadlineCheckMode === 'dryRun'}
                    onChange={() => setDeadlineCheckMode('dryRun')}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium">Dry run (recommended)</div>
                    <div className="text-sm text-gray-600">
                      Reads all opportunities/tasks and computes what would be sent.
                      <strong> Writes nothing</strong> and sends no emails. Returns a preview list.
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="deadlineCheckMode"
                    value="override"
                    checked={deadlineCheckMode === 'override'}
                    onChange={() => setDeadlineCheckMode('override')}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium">Send to override email only</div>
                    <div className="text-sm text-gray-600">
                      Real run, but ALL manager digests are redirected to a single test address.
                      Notifications are still written to Firestore as normal.
                    </div>
                    {deadlineCheckMode === 'override' && (
                      <input
                        type="email"
                        value={deadlineCheckOverrideEmail}
                        onChange={(e) => setDeadlineCheckOverrideEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-amber-500"
                      />
                    )}
                  </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="deadlineCheckMode"
                    value="live"
                    checked={deadlineCheckMode === 'live'}
                    onChange={() => setDeadlineCheckMode('live')}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-red-700">Live run (real emails)</div>
                    <div className="text-sm text-gray-600">
                      Sends one aggregated digest to every manager with overdue / due-soon items
                      right now. Same effect as the daily 8 AM ET cron.
                    </div>
                  </div>
                </label>
              </div>

              {deadlineCheckResult && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
                  <div className="font-semibold mb-2">Result</div>
                  <ul className="space-y-1 text-gray-700">
                    <li>Notifications {deadlineCheckResult.dryRun ? 'queued' : 'created'}: <strong>{deadlineCheckResult.notificationsCreated ?? 0}</strong></li>
                    <li>Manager digests {deadlineCheckResult.dryRun ? 'would be sent' : 'sent'}: <strong>{deadlineCheckResult.dryRun ? deadlineCheckResult.managersQueued : deadlineCheckResult.managerEmailsSent}</strong></li>
                    <li>Rep digests {deadlineCheckResult.dryRun ? 'would be sent' : 'sent'}: <strong>{deadlineCheckResult.dryRun ? (deadlineCheckResult.repsQueued ?? 0) : (deadlineCheckResult.repEmailsSent ?? 0)}</strong></li>
                    <li>Opportunities processed: <strong>{deadlineCheckResult.opportunitiesProcessed ?? 0}</strong></li>
                    <li>Tasks processed: <strong>{deadlineCheckResult.tasksProcessed ?? 0}</strong></li>
                    <li>Follow-ups processed: <strong>{deadlineCheckResult.followUpsProcessed ?? 0}</strong></li>
                    {deadlineCheckResult.recipientOverride && (
                      <li>Recipient override: <strong>{deadlineCheckResult.recipientOverride}</strong></li>
                    )}
                  </ul>
                  {Array.isArray(deadlineCheckResult.previews) && deadlineCheckResult.previews.length > 0 && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-indigo-700">
                        Show preview ({deadlineCheckResult.previews.length} items)
                      </summary>
                      <pre className="mt-2 max-h-64 overflow-auto bg-white p-2 border rounded text-xs">
{JSON.stringify(deadlineCheckResult.previews, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t flex justify-end gap-2">
              <button
                onClick={() => setShowDeadlineCheckModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Close
              </button>
              <button
                onClick={runDeadlineCheck}
                disabled={deadlineCheckRunning}
                className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deadlineCheckRunning
                  ? 'Running…'
                  : deadlineCheckMode === 'dryRun'
                    ? 'Run Dry Run'
                    : deadlineCheckMode === 'override'
                      ? 'Send to Override'
                      : 'Run Live'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl font-bold mb-2">Notification Settings</h2>
        <p className="text-gray-600 mb-4">
          Configure in-app notification preferences and test the notification system.
        </p>
        
        {/* Info banner about email recipients */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-blue-900 font-semibold text-sm">Email Notification Recipients Moved</p>
              <p className="text-blue-800 text-sm mt-1">
                Email recipients for order sync and customer sync notifications are now managed in the{' '}
                <a href="/admin/system-settings" className="underline font-semibold hover:text-blue-900">
                  System Settings
                </a>{' '}
                page. Look for "orderSyncRecipients" and "customerSyncRecipients" settings.
              </p>
            </div>
          </div>
        </div>

        {/* In-App Notification Testing */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">🔔 In-App Notification Testing</h3>
          <p className="text-gray-600 mb-4">
            Test the in-app notification system by creating test notifications at different severity levels.
            These will appear in your notification bell icon.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button
              onClick={() => testNotification('info')}
              disabled={testing || isReadOnly}
              className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center justify-center gap-2"
            >
              <span>ℹ️</span> Info
            </button>
            <button
              onClick={() => testNotification('success')}
              disabled={testing || isReadOnly}
              className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center justify-center gap-2"
            >
              <span>✅</span> Success
            </button>
            <button
              onClick={() => testNotification('warning')}
              disabled={testing || isReadOnly}
              className="px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center justify-center gap-2"
            >
              <span>⚠️</span> Warning
            </button>
            <button
              onClick={() => testNotification('urgent')}
              disabled={testing || isReadOnly}
              className="px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center justify-center gap-2"
            >
              <span>🚨</span> Urgent
            </button>
          </div>
        </div>

        {/* Email Report Subscription Testing */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">📧 Email Report Subscription Testing</h3>
          <p className="text-gray-600 mb-4">
            Test the email report subscription system by sending a test report email to your registered email address.
          </p>
          
          <div className="flex items-center gap-4">
            <button
              onClick={testEmailSubscription}
              disabled={testingEmail || isReadOnly}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center justify-center gap-2"
            >
              {testingEmail ? '📧 Sending...' : '📧 Send Test Report Email'}
            </button>
            {currentUser?.email && (
              <span className="text-sm text-gray-600">
                Will send to: <span className="font-semibold">{currentUser.email}</span>
              </span>
            )}
          </div>
        </div>

        {/* Admin: Run Deadline Check */}
        {can(FEATURE_PERMISSIONS.SYSTEM_SETTINGS_MANAGE) && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-xl font-semibold mb-4">📋 Deadline & Assignment Digests</h3>
            <p className="text-gray-600 mb-4">
              Manually invoke the daily digest job. Sends ONE consolidated email per manager
              <strong> and</strong> per sales rep covering overdue items, items due today/tomorrow,
              and new assignments from the last 24 hours. Use <strong>Dry Run</strong> or{' '}
              <strong>Recipient Override</strong> to safely test without spamming users.
            </p>
            <button
              onClick={() => { setDeadlineCheckResult(null); setShowDeadlineCheckModal(true); }}
              className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-semibold flex items-center gap-2"
              title="Manually run the daily deadline check (manager digest emails) with optional dry-run / recipient override"
            >
              📋 Run Deadline Check
            </button>
          </div>
        )}

        {/* Future Notification Types */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
          <h4 className="font-semibold text-gray-700 mb-2">Coming Soon</h4>
          <ul className="space-y-2 text-gray-600 text-sm">
            <li className="flex items-center">
              <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Task deadline reminders
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Opportunity stage changes
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              New order alerts
            </li>
          </ul>
        </div>
      </div>
    </main>
  );
};

export default NotificationSettingsPage;
